import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';
import type { Website } from '@/api/types';
import { Card, StatCard } from '@/design-system';
import { activeDeployment, deploymentsQuery, healthQuery, revisionLabel, type DeploymentOut } from '@/features/deploy/api';
import { themeQuery, type ThemeOut } from '@/features/designer/api';
import { domainsQuery, productionBinding } from '@/features/domains/api';
import { orgStoreQuery } from '@/features/store/api';
import { variantsQuery, type VariantOut } from '@/features/variants/api';
import { now } from '@/lib/clock';
import { daysUntil, formatDateShort, formatNumber, formatTime } from '@/lib/format';
import { LIFECYCLE } from './status';
import s from './sites.module.css';

/** «LCP p75 · mobile»'s target (DESIGN_SPEC §4.5: «цель ≤ 2,5 с»). */
const LCP_TARGET_MS = 2500;

export type StepKey = 'research' | 'generate' | 'approve' | 'publish' | 'client' | 'theme' | 'production';
export type Step = { key: StepKey; params: Record<string, string | number> };

type StepCtx = {
  site: Website;
  variants: VariantOut[] | undefined;
  theme: ThemeOut | null;
  liveDemo: DeploymentOut | null;
  activeKey: string | null;
  productionDomain: string | null;
};

/**
 * «Следующие шаги»: the first three steps the site still needs, in the TZ flow's order (research → variants →
 * approve → demo → the client's agreement → theme sync → production). The design's state (a demo live, theme
 * v8 not synced, production waiting) gives its three tiles.
 */
export function nextSteps({ site, variants, theme, liveDemo, activeKey, productionDomain }: StepCtx): Step[] {
  const at = LIFECYCLE.indexOf(site.status as (typeof LIFECYCLE)[number]);
  const before = (status: (typeof LIFECYCLE)[number]) => at >= 0 && at < LIFECYCLE.indexOf(status);
  const steps: Step[] = [];
  if (site.status === 'draft') steps.push({ key: 'research', params: {} });
  if (!variants?.length && site.status !== 'draft') steps.push({ key: 'generate', params: {} });
  if (variants?.length && !theme) steps.push({ key: 'approve', params: {} });
  if (theme && !liveDemo && before('pending_production')) steps.push({ key: 'publish', params: {} });
  if (liveDemo && before('pending_production')) steps.push({ key: 'client', params: { key: activeKey ?? '' } });
  if (site.type === 'ecommerce' && theme && theme.synced_version !== theme.version) steps.push({ key: 'theme', params: { version: theme.version } });
  if (site.status !== 'production' && site.status !== 'archived') steps.push({ key: 'production', params: { domain: productionDomain ?? '' } });
  return steps.slice(0, 3);
}

/** Обзор (DESIGN_SPEC §4.5): active variant, store, last deploy, LCP p75; the next steps. */
export function OverviewTab() {
  const site = useOutletContext<Website>();
  const { t } = useTranslation('sites');
  const { t: tc } = useTranslation('common');
  const dash = tc('dash');
  const variants = useQuery(variantsQuery(site.id));
  const theme = useQuery(themeQuery(site.id));
  const deployments = useQuery(deploymentsQuery(site.id));
  const health = useQuery(healthQuery(site.id));
  const domains = useQuery(domainsQuery(site.id));
  const store = useQuery(orgStoreQuery(site.organization.id));

  // The active variant: the website's label («B · r14»), else what the live demo serves.
  const liveDemo = activeDeployment(deployments.data);
  const label = site.variant ?? revisionLabel(liveDemo?.revision ?? null);
  const activeKey = label ? label.slice(0, 1) : null;
  const variant = variants.data?.find((v) => v.key === activeKey);
  const variantValue = !label ? dash : site.variant_failed ? t('variant.failed', { key: activeKey ?? '' }) : label;
  const brief = variant?.brief.title || dash;

  const last = deployments.data?.find((d) => d.status === 'active' || d.status === 'superseded' || d.status === 'rolled_back') ?? null;
  const lastAt = last?.activated_at ?? last?.created_at ?? null;
  const lastWhen = lastAt ? (daysUntil(lastAt, now()) === 0 ? t('overview.today') : formatDateShort(lastAt)) : '';
  const lcp = health.data?.lcp_p75_ms ?? null;
  const storeId = store.data?.external_store_id ?? null;
  const productionDomain = productionBinding(domains.data)?.hostname ?? null;

  const steps = nextSteps({ site, variants: variants.data, theme: theme.data ?? null, liveDemo, activeKey, productionDomain });
  return (
    <>
      <div className={s.tiles}>
        <StatCard size="tile" label={t('overview.active_variant')} value={variantValue} sub={brief} subTone="muted" />
        <StatCard size="tile" label={t('overview.store')} value={storeId ? `#${storeId}` : dash} sub={t('overview.store_note')} subTone="muted" />
        <StatCard
          size="tile"
          label={t('overview.last_deploy')}
          value={last ? t('overview.version', { version: last.version }) : dash}
          sub={last && lastAt ? t('overview.deploy_note', { when: lastWhen, time: formatTime(lastAt), channel: last.channel }) : t('overview.deploy_none')}
          subTone="muted"
        />
        <StatCard
          size="tile"
          label={t('overview.lcp')}
          value={lcp === null ? dash : t('overview.lcp_value', { value: formatNumber(lcp / 1000, 1) })}
          sub={t('overview.lcp_target')}
          subTone={lcp !== null && lcp > LCP_TARGET_MS ? 'accent' : 'ok'}
        />
      </div>
      <Card className={s.next}>
        <span className={s.nextTitle}>{t('overview.next')}</span>
        {steps.length ? (
          <ol className={s.steps}>
            {steps.map((st, i) => (
              <li key={st.key} className={s.step}>
                <span className={s.stepNo}>{String(i + 1).padStart(2, '0')}</span>
                <span className={s.stepTitle}>{t(`overview.steps.${st.key}.title`, { ...st.params, domain: st.params.domain || t('overview.production_domain') })}</span>
                <span className={s.stepText}>{t(`overview.steps.${st.key}.text`, { ...st.params, domain: st.params.domain || t('overview.production_domain') })}</span>
              </li>
            ))}
          </ol>
        ) : (
          <span className={s.stepText}>{t('overview.steps_done')}</span>
        )}
      </Card>
    </>
  );
}
